/* eslint-disable no-param-reassign */
import type { RefObject } from 'react';

import { useLayoutEffect } from 'react';

import type { Size } from '~/utilities/graphql/codegen';

/**
 * Makes all child elements of a specific container the width of the maximum child size
 */
export const useSizeButtonsStyle = ({
  parrentRef,
  minWidth,
  skip = false,
  selectedSize,
  parsedLengthSizes,
}: {
  parrentRef: RefObject<HTMLElement | null>;
  minWidth?: number;
  skip?: boolean;
  selectedSize: Size | null | undefined;
  parsedLengthSizes: object;
}) => {
  useLayoutEffect(() => {
    if (skip || !parrentRef?.current) {
      return;
    }

    const container = parrentRef.current;
    const items = Array.from(container?.children) as HTMLElement[];

    /* set container & child elements to block sizing to get their actual size */
    container.style.display = 'block';

    items.forEach(item => {
      item.style.display = 'inline-block';
      item.style.width = 'auto';
    });

    const maxWidth = Math.max(...items.map(({ offsetWidth }) => offsetWidth));
    const elementWidth = minWidth ? Math.max(minWidth, maxWidth) : maxWidth;

    /* revert back the styling to grid once we got the max width possible for child elements */
    container.style.display = 'grid';
    container.style.gridTemplateColumns = `repeat(auto-fit, ${elementWidth}px)`;
    container.style.opacity = `1`;
    container.style.gridAutoFlow = 'unset';

    items.forEach(item => {
      if (JSON.parse(item.getAttribute('data-disabled') || 'false')) {
        const decorativeElement = item.querySelector('hr');

        if (decorativeElement) {
          const { width, height } = item.getBoundingClientRect();
          // calculate the bottom-left to top-right diagonal angle from bottom base of rectangle
          const angle = Math.atan2(height, width) * (180 / Math.PI) * -1;

          decorativeElement.style.transform = `rotate(${angle.toFixed(2)}deg) scaleX(1.414)`;
        }
      }

      item.style.removeProperty('display');
      item.style.removeProperty('width');
    });
  }, [parrentRef, minWidth, skip, selectedSize, parsedLengthSizes]);
};
